import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  StatCard,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  EmptyState,
  Button,
  FormGroup,
  Input,
  TabGroup,
  Badge,
  AlertBanner,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface LoyaltyCustomer {
  id: string;
  phone: string;
  first_name: string;
  last_name: string;
  email: string;
  birthday: string;
  marketing_opt_in_sms: boolean;
  points_balance: number;
  lifetime_spend: number;
  last_visit: string;
  loyalty_history?: LoyaltyHistoryEntry[];
}

interface LoyaltyHistoryEntry {
  id: string;
  type: "earned" | "redeemed" | "bonus";
  points: number;
  description: string;
  created_at: string;
}

interface LoyaltySettings {
  points_per_dollar: number;
  redemption_threshold: number;
  redemption_value: number;
  birthday_bonus_points: number;
  win_back_days: number;
}

// ============================================================
// HELPERS
// ============================================================

function formatDate(d: string): string {
  if (!d) return "--";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCurrency(n: number | string | null | undefined): string {
  // Postgres numeric columns (lifetime_spend, balances) arrive as strings;
  // "12.50".toFixed is not a function and took the whole app down (10/6).
  return "$" + (Number(n) || 0).toFixed(2);
}

function daysSince(d: string): number {
  if (!d) return 999;
  const diff = Date.now() - new Date(d).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

// ============================================================
// CUSTOMERS TAB
// ============================================================

function CustomersTab() {
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState<LoyaltyCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedHistory, setExpandedHistory] = useState<LoyaltyHistoryEntry[]>([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    phone: "",
    first_name: "",
    last_name: "",
    email: "",
    birthday: "",
    marketing_opt_in_sms: true,
  });
  const [saving, setSaving] = useState(false);

  const fetchCustomers = useCallback(async (q: string, lim: number) => {
    setLoading(true);
    try {
      const res = await api.get("/loyalty/customers", { params: { search: q, limit: lim } });
      // the API returns { customers, total }; tolerate the old bare array
      const rows = Array.isArray(res.data) ? res.data : res.data.customers || [];
      setCustomers(rows);
      setTotal(Array.isArray(res.data) ? rows.length : Number(res.data.total || rows.length));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers("", limit);
  }, [fetchCustomers, limit]);

  const handleSearch = () => {
    fetchCustomers(search, limit);
  };

  const handleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    try {
      const res = await api.get(`/loyalty/customers/${id}`);
      setExpandedHistory(res.data.loyalty_history || []);
    } catch {
      setExpandedHistory([]);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.post("/loyalty/customers", newCustomer);
      setShowNewForm(false);
      setNewCustomer({ phone: "", first_name: "", last_name: "", email: "", birthday: "", marketing_opt_in_sms: true });
      fetchCustomers(search, limit);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <Input
            value={search}
            onChange={setSearch}
            placeholder="Search by phone, name, or email..."
          />
        </div>
        <Button onClick={handleSearch} variant="secondary" size="sm">Search</Button>
        <Button onClick={() => setShowNewForm(!showNewForm)} variant="primary" size="sm">
          {showNewForm ? "Cancel" : "New Customer"}
        </Button>
      </div>

      {showNewForm && (
        <Card className="mb-6">
          <CardHeader title="New Customer" />
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="Phone">
              <Input value={newCustomer.phone} onChange={(v) => setNewCustomer({ ...newCustomer, phone: v })} placeholder="717-555-1234" required />
            </FormGroup>
            <FormGroup label="First Name">
              <Input value={newCustomer.first_name} onChange={(v) => setNewCustomer({ ...newCustomer, first_name: v })} required />
            </FormGroup>
            <FormGroup label="Last Name">
              <Input value={newCustomer.last_name} onChange={(v) => setNewCustomer({ ...newCustomer, last_name: v })} required />
            </FormGroup>
            <FormGroup label="Email">
              <Input value={newCustomer.email} onChange={(v) => setNewCustomer({ ...newCustomer, email: v })} type="email" />
            </FormGroup>
            <FormGroup label="Birthday">
              <Input value={newCustomer.birthday} onChange={(v) => setNewCustomer({ ...newCustomer, birthday: v })} type="date" />
            </FormGroup>
            <FormGroup label="SMS Marketing Opt-in">
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newCustomer.marketing_opt_in_sms}
                  onChange={(e) => setNewCustomer({ ...newCustomer, marketing_opt_in_sms: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm" style={{ color: "var(--rbw-navy)" }}>Opted in</span>
              </label>
            </FormGroup>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleCreate} disabled={saving || !newCustomer.phone || !newCustomer.first_name}>
              {saving ? "Saving..." : "Create Customer"}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <EmptyState message="Loading customers..." icon="..." />
      ) : customers.length === 0 ? (
        <EmptyState message="No customers found" detail="Try a different search or add a new customer" />
      ) : (
        <>
        <div className="text-xs mb-2" style={{ color: "var(--rbw-muted)" }}>
          Showing {customers.length.toLocaleString()} of {total.toLocaleString()} customer{total !== 1 ? "s" : ""}
        </div>
        <Table>
          <Thead>
            <Th>Name</Th>
            <Th>Phone</Th>
            <Th align="right">Points</Th>
            <Th align="right">Lifetime Spend</Th>
            <Th>Last Visit</Th>
            <Th>Birthday</Th>
          </Thead>
          <tbody>
            {customers.map((c, i) => (
              <React.Fragment key={c.id}>
                <TRow index={i}>
                  <Td bold>
                    <button
                      onClick={() => handleExpand(c.id)}
                      className="text-left font-semibold transition-colors"
                      style={{ color: "var(--rbw-navy)" }}
                    >
                      {c.first_name} {c.last_name}
                      <span className="ml-2 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                        {expandedId === c.id ? "\u25B2" : "\u25BC"}
                      </span>
                    </button>
                  </Td>
                  <Td>{c.phone}</Td>
                  <Td align="right">
                    <Badge variant="gold">{c.points_balance} pts</Badge>
                  </Td>
                  <Td align="right">{formatCurrency(c.lifetime_spend)}</Td>
                  <Td>{formatDate(c.last_visit)}</Td>
                  <Td>{c.birthday ? formatDate(c.birthday) : "--"}</Td>
                </TRow>
                {expandedId === c.id && (
                  <tr>
                    <td colSpan={6} className="px-5 py-4" style={{ background: "var(--rbw-bg)" }}>
                      <div className="text-sm font-semibold mb-3" style={{ color: "var(--rbw-navy)" }}>
                        Loyalty History
                      </div>
                      {expandedHistory.length === 0 ? (
                        <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>No history yet</div>
                      ) : (
                        <div className="space-y-2">
                          {expandedHistory.map((h) => (
                            <div key={h.id} className="flex items-center gap-4 text-sm">
                              <Badge variant={h.type === "earned" ? "green" : h.type === "redeemed" ? "blue" : "gold"}>
                                {h.type}
                              </Badge>
                              <span style={{ color: h.type === "redeemed" ? "var(--rbw-danger)" : "var(--rbw-success)" }}>
                                {h.type === "redeemed" ? "-" : "+"}{h.points} pts
                              </span>
                              <span style={{ color: "var(--rbw-gray-dark)" }}>{h.description}</span>
                              <span className="ml-auto text-xs" style={{ color: "var(--rbw-gray)" }}>
                                {formatDate(h.created_at)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
        {customers.length < total && (
          <div className="mt-3 text-center">
            <Button variant="secondary" onClick={() => setLimit(limit + 200)} disabled={loading}>
              Show {Math.min(200, total - customers.length)} more
            </Button>
          </div>
        )}
        </>
      )}
    </div>
  );
}

// ============================================================
// BIRTHDAYS TAB
// ============================================================

function BirthdaysTab() {
  const [customers, setCustomers] = useState<LoyaltyCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);

  useEffect(() => {
    api.get("/loyalty/birthdays")
      .then((res) => setCustomers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSendBonus = async (customerId: string) => {
    setSendingId(customerId);
    try {
      await api.post("/loyalty/bonus", {
        customer_id: customerId,
        points: 0, // server uses birthday_bonus_points setting
        description: "Birthday bonus",
      });
      // Refresh
      const res = await api.get("/loyalty/birthdays");
      setCustomers(res.data);
    } catch {
      // ignore
    } finally {
      setSendingId(null);
    }
  };

  if (loading) return <EmptyState message="Loading..." icon="..." />;
  if (customers.length === 0) return <EmptyState message="No birthdays this month" detail="Check back next month" />;

  return (
    <div>
      <AlertBanner variant="gold">
        <strong>{customers.length}</strong> customer{customers.length !== 1 ? "s" : ""} with birthdays this month
      </AlertBanner>
      <Table>
        <Thead>
          <Th>Name</Th>
          <Th>Phone</Th>
          <Th>Birthday</Th>
          <Th align="right">Points</Th>
          <Th align="right">Action</Th>
        </Thead>
        <tbody>
          {customers.map((c, i) => (
            <TRow key={c.id} index={i}>
              <Td bold>{c.first_name} {c.last_name}</Td>
              <Td>{c.phone}</Td>
              <Td>{formatDate(c.birthday)}</Td>
              <Td align="right">
                <Badge variant="gold">{c.points_balance} pts</Badge>
              </Td>
              <Td align="right">
                <Button
                  onClick={() => handleSendBonus(c.id)}
                  size="sm"
                  variant="gold"
                  disabled={sendingId === c.id}
                >
                  {sendingId === c.id ? "Sending..." : "Send Bonus"}
                </Button>
              </Td>
            </TRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ============================================================
// WIN-BACK TAB
// ============================================================

function WinBackTab() {
  const [customers, setCustomers] = useState<LoyaltyCustomer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/loyalty/win-back")
      .then((res) => setCustomers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <EmptyState message="Loading..." icon="..." />;
  if (customers.length === 0) return <EmptyState message="No win-back customers" detail="All customers have visited recently" />;

  return (
    <div>
      <AlertBanner variant="danger">
        <strong>{customers.length}</strong> customer{customers.length !== 1 ? "s" : ""} haven't visited in 30+ days
      </AlertBanner>
      <Table>
        <Thead>
          <Th>Name</Th>
          <Th>Phone</Th>
          <Th>Last Visit</Th>
          <Th align="right">Days Since</Th>
          <Th align="right">Lifetime Spend</Th>
        </Thead>
        <tbody>
          {customers.map((c, i) => (
            <TRow key={c.id} index={i}>
              <Td bold>{c.first_name} {c.last_name}</Td>
              <Td>{c.phone}</Td>
              <Td>{formatDate(c.last_visit)}</Td>
              <Td align="right">
                <Badge variant="danger">{daysSince(c.last_visit)} days</Badge>
              </Td>
              <Td align="right">{formatCurrency(c.lifetime_spend)}</Td>
            </TRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ============================================================
// SETTINGS TAB (owner only)
// ============================================================

function SettingsTab() {
  const [settings, setSettings] = useState<LoyaltySettings>({
    points_per_dollar: 1,
    redemption_threshold: 100,
    redemption_value: 5,
    birthday_bonus_points: 50,
    win_back_days: 30,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/loyalty/settings")
      .then((res) => setSettings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/loyalty/settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <EmptyState message="Loading settings..." icon="..." />;

  return (
    <Card>
      <CardHeader title="Loyalty Program Settings" />
      {saved && <AlertBanner variant="green">Settings saved successfully</AlertBanner>}
      <div className="grid grid-cols-2 gap-4">
        <FormGroup label="Points per Dollar Spent">
          <Input
            value={settings.points_per_dollar}
            onChange={(v) => setSettings({ ...settings, points_per_dollar: Number(v) })}
            type="number"
            min="0"
            step="0.1"
          />
        </FormGroup>
        <FormGroup label="Redemption Threshold (points)">
          <Input
            value={settings.redemption_threshold}
            onChange={(v) => setSettings({ ...settings, redemption_threshold: Number(v) })}
            type="number"
            min="1"
          />
        </FormGroup>
        <FormGroup label="Redemption Value ($)">
          <Input
            value={settings.redemption_value}
            onChange={(v) => setSettings({ ...settings, redemption_value: Number(v) })}
            type="number"
            min="0"
            step="0.01"
          />
        </FormGroup>
        <FormGroup label="Birthday Bonus Points">
          <Input
            value={settings.birthday_bonus_points}
            onChange={(v) => setSettings({ ...settings, birthday_bonus_points: Number(v) })}
            type="number"
            min="0"
          />
        </FormGroup>
        <FormGroup label="Win-Back Days (inactive threshold)">
          <Input
            value={settings.win_back_days}
            onChange={(v) => setSettings({ ...settings, win_back_days: Number(v) })}
            type="number"
            min="1"
          />
        </FormGroup>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </Card>
  );
}

// ============================================================
// MAIN LOYALTY PAGE
// ============================================================

export default function LoyaltyPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const tabs = [
    { key: "customers", label: "Customers" },
    { key: "birthdays", label: "Birthdays" },
    { key: "winback", label: "Win-Back" },
    ...(isOwner ? [{ key: "settings", label: "Settings" }] : []),
  ];

  const [activeTab, setActiveTab] = useState("customers");

  return (
    <PageContainer>
      <PageHeader
        title="Customers & Loyalty"
        subtitle="Manage loyalty program members, birthdays, and win-back campaigns"
      />
      <TabGroup tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === "customers" && <CustomersTab />}
      {activeTab === "birthdays" && <BirthdaysTab />}
      {activeTab === "winback" && <WinBackTab />}
      {activeTab === "settings" && isOwner && <SettingsTab />}
    </PageContainer>
  );
}
